/**
 * MonthlyReportSection
 *
 * 월별 리포트 섹션
 * - LEFT side sticky navigation (AlphabetIndexBar 스타일)
 * - 모든 월별 섹션 펼쳐서 표시
 * - IntersectionObserver로 현재 보이는 월 감지
 * - 클릭 시 해당 월 섹션으로 앵커 이동
 * - LineGrid + EditorPickCard 스타일
 * - 월별 통계 (조회수, 댓글, 리포스트, 좋아요) + 전월 대비 증감
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Box, Stack, Button, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import LineGrid from '../magazine/LineGrid';
import EditorPickCard from '../magazine/EditorPickCard';
import { useIsMobile } from '../../hooks/useIsMobile';
import YearEndSectionTitle from './YearEndSectionTitle';

/**
 * 월 선택 LEFT Navigation (AlphabetIndexBar 스타일)
 * - 현재 보이는 월 하이라이트
 * - 클릭 시 해당 월로 앵커 이동
 */
const MonthIndexBar = ({
  months,
  activeMonth,
  onClick,
  offset = 64,
  hiddenOnMobile = true,
}) => {
  // 활동이 있는 월만 표시
  const activeMonths = months.filter((m) => m.totalThreads > 0);
  const totalThreads = months.reduce((sum, m) => sum + (m.totalThreads || 0), 0);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: offset,
        height: `calc(100vh - ${offset}px)`,
        display: hiddenOnMobile ? { xs: 'none', xl: 'flex' } : 'flex',
        alignItems: 'center',
        px: { xs: 0, xl: 1 },
        flexShrink: 0,
        alignSelf: 'flex-start',
        zIndex: (theme) => theme.zIndex.appBar - 2,
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={1} alignItems="center">
        {activeMonths.map((monthData) => {
          const monthNumber = parseInt(monthData.month, 10);
          const isSelected = activeMonth === monthData.month;
          const targetId = `month-section-${monthData.month}`;

          return (
            <Button
              key={monthData.month}
              variant="text"
              size="small"
              onClick={() => onClick(monthData.month, targetId)}
              sx={{
                minWidth: 0,
                width: 48,
                height: 32,
                p: 0,
                color: isSelected ? 'primary.main' : 'text.secondary',
                fontWeight: isSelected ? 800 : 600,
                fontSize: '0.75rem',
              }}
            >
              {monthNumber}월
            </Button>
          );
        })}

        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', mt: 0.5, fontSize: '0.625rem' }}
        >
          {totalThreads}
        </Typography>
      </Stack>
    </Box>
  );
};

/**
 * 월별 통계 표시 컴포넌트
 * - 1줄: 조회 | 댓글 | 리포스트 | 좋아요
 * - 2줄: 조회수 증감 | 참여량 증감 (1월 제외)
 * - primary 색상만 사용
 */
const MonthStats = ({ stats, prevStats, isFirstMonth, isMobile }) => {
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const getChange = (current, prev) => {
    if (!prev || prev === 0) return null;
    return ((current - prev) / prev) * 100;
  };

  const formatChange = (change) => {
    if (change === null || isNaN(change) || !isFinite(change)) return '';
    const sign = change > 0 ? '+' : '';
    return `${sign}${Math.round(change)}%`;
  };

  // 참여량 = 댓글 + 리포스트 + 좋아요
  const currentEngagement = (stats.replies || 0) + (stats.reposts || 0) + (stats.likes || 0);
  const prevEngagement = prevStats
    ? (prevStats.replies || 0) + (prevStats.reposts || 0) + (prevStats.likes || 0)
    : 0;

  const viewsChange = !isFirstMonth ? getChange(stats.views, prevStats?.views) : null;
  const engagementChange = !isFirstMonth ? getChange(currentEngagement, prevEngagement) : null;

  const statItems = [
    { label: '조회', value: stats.views },
    { label: '댓글', value: stats.replies },
    { label: '리포스트', value: stats.reposts },
    { label: '좋아요', value: stats.likes },
  ];

  return (
    <Box>
      {/* 1줄: 통계 수치 */}
      <Typography
        variant="body2"
        component="div"
        sx={{
          color: 'primary.main',
          fontSize: isMobile ? '0.7rem' : '0.75rem',
          fontWeight: 500,
          letterSpacing: '0.02em',
          opacity: 0.85,
        }}
      >
        {statItems.map((item, idx) => (
          <span key={item.label}>
            {idx > 0 && (
              <span style={{ opacity: 0.4, margin: '0 6px' }}>|</span>
            )}
            {item.label} {formatNumber(item.value)}
          </span>
        ))}
      </Typography>

      {/* 2줄: 증감 (1월 제외) */}
      {!isFirstMonth && (viewsChange !== null || engagementChange !== null) && (
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: 'primary.main',
            fontSize: isMobile ? '0.65rem' : '0.7rem',
            fontWeight: 500,
            letterSpacing: '0.02em',
            opacity: 0.6,
            mt: 0.3,
          }}
        >
          {formatChange(viewsChange) && (
            <span>조회수 {formatChange(viewsChange)}</span>
          )}
          {formatChange(viewsChange) && formatChange(engagementChange) && (
            <span style={{ opacity: 0.5, margin: '0 6px' }}>|</span>
          )}
          {formatChange(engagementChange) && (
            <span>참여량 {formatChange(engagementChange)}</span>
          )}
        </Typography>
      )}
    </Box>
  );
};

/**
 * 월별 통계 계산 함수
 */
const calculateMonthStats = (threads) => {
  if (!threads || threads.length === 0) {
    return { views: 0, replies: 0, reposts: 0, likes: 0 };
  }

  return threads.reduce(
    (acc, thread) => ({
      views: acc.views + (thread.views || 0),
      replies: acc.replies + (thread.replies || 0),
      reposts: acc.reposts + (thread.reposts || 0),
      likes: acc.likes + (thread.likes || 0),
    }),
    { views: 0, replies: 0, reposts: 0, likes: 0 }
  );
};

export const MonthlyReportSection = ({ monthlyReport, mostActiveMonth, ownerProfile }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile('xl');

  // 현재 보이는 월 (IntersectionObserver로 감지)
  const [activeMonth, setActiveMonth] = useState(null);

  // 월별 데이터를 배열로 변환
  const monthsArray = useMemo(() => {
    if (!monthlyReport) return [];

    return Object.entries(monthlyReport)
      .map(([month, data]) => ({
        month,
        ...data,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [monthlyReport]);

  // 활동이 있는 월만 필터링
  const activeMonthsArray = useMemo(() => {
    return monthsArray.filter((m) => m.totalThreads > 0);
  }, [monthsArray]);

  // 월별 통계 계산 (전월 대비 비교용)
  const monthStatsMap = useMemo(() => {
    const statsMap = {};
    monthsArray.forEach((monthData) => {
      statsMap[monthData.month] = calculateMonthStats(monthData.topThreads);
    });
    return statsMap;
  }, [monthsArray]);

  // 헤더 높이 계산 (sticky 위치용)
  const HEADER_HEIGHT = isMobile ? 56 : 64;

  // IntersectionObserver로 현재 보이는 월 섹션 감지
  useEffect(() => {
    const heads = document.querySelectorAll('[data-month-head="1"]');
    if (!heads || heads.length === 0) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
        if (visible.length > 0) {
          const key = visible[0].target.getAttribute('data-month-key');
          if (key) setActiveMonth(key);
        }
      },
      {
        root: null,
        rootMargin: `-${HEADER_HEIGHT + 8}px 0px -80% 0px`,
        threshold: [0, 0.1],
      }
    );

    heads.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [activeMonthsArray, HEADER_HEIGHT]);

  // 클릭 시 해당 월 섹션으로 앵커 이동
  const handleIndexClick = (month, targetId) => {
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 활동이 있는 월이 하나도 없으면 표시 안 함
  const hasActivity = monthsArray.some((m) => m.totalThreads > 0);

  if (!monthlyReport || !hasActivity) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        borderBottom: '1px solid',
        borderColor: 'primary.main',
      }}
    >
      {/* 섹션 타이틀 */}
      <YearEndSectionTitle title={t('yearEnd.monthlyReport.title')} />

      {/* LEFT Navigation + Content Layout - overflow visible로 sticky 작동 보장 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xs: 1, xl: 2 },
          maxWidth: '100%',
          py: isMobile ? 2 : 3,
        }}
      >
        {/* LEFT: Month Index Bar - sticky */}
        <MonthIndexBar
          months={monthsArray}
          activeMonth={activeMonth}
          onClick={handleIndexClick}
          offset={HEADER_HEIGHT}
          hiddenOnMobile={true}
        />

        {/* RIGHT: 모든 월별 섹션 펼쳐서 표시 */}
        <Box sx={{ flex: 1, minWidth: 0, overflowX: 'hidden', width: '100%' }}>
          {activeMonthsArray.map((monthData, monthIndex) => {
            const monthNumber = parseInt(monthData.month, 10);
            const threads = monthData.topThreads || [];
            const sectionId = `month-section-${monthData.month}`;

            // 이전 월 키 계산 (01 → 이전월 없음, 02 → 01, ...)
            const prevMonthKey = monthNumber > 1
              ? String(monthNumber - 1).padStart(2, '0')
              : null;
            const currentStats = monthStatsMap[monthData.month];
            const prevStats = prevMonthKey ? monthStatsMap[prevMonthKey] : null;
            const isFirstMonth = monthIndex === 0;

            return (
              <Box
                key={monthData.month}
                sx={{
                  mb: 4,
                  pt: 2,
                  pb: 0,
                }}
              >
                {/* 월 헤더 + 통계 */}
                <Box
                  id={sectionId}
                  data-month-head="1"
                  data-month-key={monthData.month}
                  sx={{
                    mb: 2,
                    scrollMarginTop: `${HEADER_HEIGHT + 12}px`,
                  }}
                >
                  {/* 월 타이틀 */}
                  <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    fontWeight={700}
                    sx={{
                      color: 'primary.main',
                      letterSpacing: '0.05em',
                      fontFamily: '"RIDIBatang", "Noto Serif KR", serif',
                      mb: 0.5,
                    }}
                  >
                    {t(`common.month.${monthNumber}`)}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: 'primary.main', fontWeight: 600, ml: 0.5, opacity: 0.7 }}
                    >
                      ({threads.length})
                    </Typography>
                  </Typography>

                  {/* 월별 통계 */}
                  <MonthStats
                    stats={currentStats}
                    prevStats={prevStats}
                    isFirstMonth={isFirstMonth}
                    isMobile={isMobile}
                  />
                </Box>

                {/* 스레드 그리드 - LineGrid + 에디터리얼 카드 (월별 4개, 랭킹 표시) */}
                {threads.length > 0 ? (
                  <Box sx={{ borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'primary.main' }}>
                    <LineGrid
                      gap={0}
                      borderColor="primary.main"
                      gridColumns={isMobile ? 2 : 4}
                      breakpoint="xl"
                    >
                      {threads.slice(0, 4).map((thread, idx) => (
                        <Grid key={thread.id || idx} size={{ xs: 6, xl: 3 }}>
                          <Box sx={{ position: 'relative', height: '100%' }}>
                            {/* 랭킹 배지 (우측 상단) */}
                            <Typography
                              variant="overline"
                              sx={{
                                position: 'absolute',
                                top: isMobile ? 8 : 12,
                                right: isMobile ? 8 : 12,
                                zIndex: 1,
                                color: 'primary.main',
                                fontWeight: 900,
                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                letterSpacing: '0.1em',
                              }}
                            >
                              #{idx + 1}
                            </Typography>
                            <EditorPickCard
                              thread={thread}
                              isMobile={isMobile}
                              userID={ownerProfile?.id}
                              username={ownerProfile?.username}
                              profileImage={ownerProfile?.threads_profile_picture_url}
                              showUser={false}
                              showDate={true}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </LineGrid>
                  </Box>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      {t('yearEnd.timeline.noActivity')}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default MonthlyReportSection;
